import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface TemporaryReferenceData {
  initialImage?: string; // Object URL for preview
  imageBlob?: File; // Actual file data
  source: 'paste' | 'drag-drop' | 'web-share-target';
  title?: string;
  description?: string;
  sourceURL?: string;
  category?: string;
  timestamp: number;
}

interface ReferenceDataContextType {
  temporaryData: TemporaryReferenceData | null;
  setTemporaryData: (data: TemporaryReferenceData | null) => void;
  clearTemporaryData: () => void;
  hasTemporaryData: boolean;
}

const ReferenceDataContext = createContext<ReferenceDataContextType | undefined>(undefined);

export function ReferenceDataProvider({ children }: { children: ReactNode }) {
  const [temporaryData, setTemporaryDataState] = useState<TemporaryReferenceData | null>(null);

  const setTemporaryData = useCallback((data: TemporaryReferenceData | null) => {
    // Clean up previous object URL if it exists
    if (temporaryData?.initialImage && temporaryData.initialImage.startsWith('blob:')) {
      URL.revokeObjectURL(temporaryData.initialImage);
    }
    setTemporaryDataState(data);
  }, [temporaryData]);

  const clearTemporaryData = useCallback(() => {
    if (temporaryData?.initialImage && temporaryData.initialImage.startsWith('blob:')) {
      URL.revokeObjectURL(temporaryData.initialImage);
    }
    setTemporaryDataState(null);
  }, [temporaryData]);

  const hasTemporaryData = temporaryData !== null;

  return (
    <ReferenceDataContext.Provider value={{
      temporaryData,
      setTemporaryData,
      clearTemporaryData,
      hasTemporaryData
    }}>
      {children}
    </ReferenceDataContext.Provider>
  );
}

export function useReferenceData() {
  const context = useContext(ReferenceDataContext);
  if (context === undefined) {
    throw new Error('useReferenceData must be used within a ReferenceDataProvider');
  }
  return context;
} 